/* Copyright (c) 2020-present CloudIO Inc */

import {
  action,
  useStateXAtom,
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXWritableSelector,
} from '@cloudio-saas/statex';
import { IconButton } from '@mui/material';
import useWindowSize from 'hooks/useWindowSize';
import Icon from 'icons/Icon';
import {
  activePinnedSidebarPanelSelector,
  activeSidebarPanelAtom,
  SidebarPanelName,
} from 'layout/Sidebar/atoms';
import React, { memo } from 'react';
import useUser, { userAtom } from 'root/useUser';
import {
  maximizeSidebarWidthAtom,
  pinnedSidebarWidthAtom,
  sidebarWidthAtom,
} from 'utils/state';
import { useAppID } from '../../../designer/atoms';
import { usePlatform } from '../../../platform/PlatformHooks';
import { IconPulse } from '../../../utils/Pulse';
import { ShareUrl } from '../../../utils/url';

export const sidebarToggleAction = action(
  (
    { get, set },
    {
      open,
      pinned = false,
      panel,
    }: { open: boolean; pinned?: boolean; panel?: SidebarPanelName },
  ) => {
    const { userName } = get(userAtom) ?? { userName: 'GUEST' };
    const pinnedSidebarPanel = get(activePinnedSidebarPanelSelector, {
      params: { userName },
    });
    if (open) {
      if (panel) {
        set(maximizeSidebarWidthAtom, 'Y');
        if (pinned) {
          set(activePinnedSidebarPanelSelector, panel, {
            params: { userName },
          });
        } else {
          set(activeSidebarPanelAtom, panel);
        }
      }
    } else {
      set(maximizeSidebarWidthAtom, 'N');
      if (pinned && pinnedSidebarPanel && pinnedSidebarPanel !== 'HIDDEN') {
        set(activePinnedSidebarPanelSelector, 'HIDDEN', {
          params: { userName },
        });
      } else if (!pinned) {
        set(activeSidebarPanelAtom, 'HIDDEN');
      }
    }
  },
);

function ExpandCollapseIcon({
  showOnlyWhenMaximized = false,
  pinned = false,
  hideShareUrl,
  standalone,
}: {
  showOnlyWhenMaximized?: boolean;
  pinned?: boolean;
  hideShareUrl?: boolean;
  standalone?: boolean;
}): React.ReactElement {
  const [maximized, setMaximized] = useStateXAtom(maximizeSidebarWidthAtom);
  const appUid = useAppID();
  const platform = usePlatform();
  const user = useUser();
  const { userName } = user;
  const [pinnedSidebarPanel, setPinnedSidebarPanel] = useStateXWritableSelector(
    activePinnedSidebarPanelSelector,
    {
      params: { userName },
    },
  );
  const setSidebarPanel = useStateXAtomSetter(activeSidebarPanelAtom);

  const actualWidth = useStateXAtomValue(
    pinned ? pinnedSidebarWidthAtom : sidebarWidthAtom,
    { params: { userName } },
  );

  const [ww] = useWindowSize();
  const close = pinned || actualWidth >= ww - 80 || ww - 40 < 600;

  if (showOnlyWhenMaximized && maximized !== 'Y' && !close) {
    return <></>;
  }

  return (
    <>
      {pinned || hideShareUrl ? null : (
        <ShareUrl platform={platform} appUid={appUid} user={user} />
      )}
      {pinned || standalone ? null : (
        <IconButton
          data-testid={`${pinned ? 'pinned-' : ''}panel-${
            maximized === 'Y' ? 'collapse' : 'expand'
          }`}
          color="primary"
          aria-label={maximized === 'Y' ? 'Minimize' : 'Maximize'}
          onClick={() => {
            setMaximized(maximized !== 'Y' ? 'Y' : 'N');
          }}
          data-tip={maximized === 'Y' ? 'Minimize' : 'Maximize'}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            {maximized === 'Y' ? 'Minimize' : 'Maximize'}
          </span>
          <Icon iconName={maximized === 'Y' ? 'compress-alt' : 'expand-alt'} />
          <IconPulse
            pulseKey={maximized === 'Y' ? 'sb_min' : 'sb_max'}
            pulseMessage={`Click here ${
              maximized === 'Y' ? 'minimize' : 'maximize'
            } the sidebar panel.`}
          />
        </IconButton>
      )}
      {standalone ? null : (
        <IconButton
          data-testid={`${pinned ? 'pinned-' : ''}panel-close`}
          color="primary"
          aria-label="Close"
          onClick={() => {
            setMaximized('N');
            if (
              pinned &&
              pinnedSidebarPanel &&
              pinnedSidebarPanel !== 'HIDDEN'
            ) {
              setPinnedSidebarPanel('HIDDEN');
            } else if (!pinned) {
              setSidebarPanel('HIDDEN');
            }
          }}
          data-tip="Close"
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      )}
    </>
  );
}

export default memo(ExpandCollapseIcon);
